import React, {useCallback, useEffect, useRef, useState} from 'react';
import styled, {keyframes, useTheme} from 'styled-components';
import {tr} from '../../../translation';
import GenericInput from '../../../components/textInput';
import {Theme} from '../../../theme/lightTheme';
import {checkIfFormValid, handleChange} from '../../../utils/form';
import DatePicker from '../../../components/datePicker';
import Picker from '../../../components/picker';
import useScrollTop from '../../../utils/hooks/useScrollTop';
import {useSetMainLayoutProps} from '../../../route/routeMainLayout/context';
import AnimatedRouterLayout from '../../../components/animatedRouterLayout';
import {showGlobalModal} from '../../../connected-components/globalModal/actions';
import {endSession} from '../../auth/login/actions';
import {
  getDomains,
  getSpeciality,
  getTypeOfStudy,
  scrapeArticle,
} from '../scrapeArticles/actions';
import {
  DomainsData,
  ScrapeBody,
  SpecialityData,
  TypeOfStudyData,
} from '../../../interfaces/api/dashboard/scrapeArticle';
import {isSmallHeight} from '../../../utils/responsive';
import moment from 'moment';
import {motion} from 'framer-motion';
import {AiOutlineCheck, AiFillLock, AiOutlineClose} from 'react-icons/ai';
import GenioAnimation from '../../../components/genioAnimation';
import TypingAnimation from '../../../components/typingAnimation';
import GenericButton from '../../../components/button';
import {IoIosArrowBack} from 'react-icons/io';
import ScrapeRobotAnimation from '../../../components/scrapeRobotAnimation';
import {useGlobalAudioPlayer} from 'react-use-audio-player';
import Icon from '../../../components/icon';
import MyPressable from '../../../components/myPressable';
import {RiDeleteBin6Line} from 'react-icons/ri';
import {
  genioResearchFlow,
  genioResearchFormFields,
  getFieldResearchIndex,
  getNextResearchField,
  getNextResearchFieldIndex,
} from '../../../assets/static/scrapeArticles';
import {IoVolumeHighOutline, IoVolumeMuteOutline} from 'react-icons/io5';
import {openUrl} from '../../../utils/helper';

const iconSize = (isMobile: boolean) =>
  isMobile ? '2rem' : isSmallHeight ? '2rem' : '2.5rem';

export const zoomIn = keyframes`
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
`;

export const zoomOut = keyframes`
  from {
    transform: scale(1);
  }
  to {
    transform: scale(0);
  }
`;

const AnimatedDiv = styled.div<{isZoomedIn?: boolean}>`
  animation: ${({isZoomedIn}) => (isZoomedIn ? zoomIn : zoomOut)} 0.3s
    ease-in-out forwards;
  transform-origin: center;
  display: ${({isZoomedIn}) => (isZoomedIn ? 'flex' : 'none')};
`;

const _renderClearPicker = (
  label: string,
  form: any,
  updateForm: (props: any) => void,
) => {
  const theme = useTheme() as Theme;

  return (
    <AnimatedDiv isZoomedIn={label && (form?.[label] || [])?.length > 0}>
      <MyPressable
        onClick={event => {
          event.stopPropagation();
          handleChange(label, [], updateForm);
        }}
        disableRipple>
        <RiDeleteBin6Line
          size={theme.text.s6}
          color={theme.text.error + 'AA'}
        />
      </MyPressable>
    </AnimatedDiv>
  );
};

const Container = styled(AnimatedRouterLayout)`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
`;
const HandlerContainer = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
`;
const ResultContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  width: 100%;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  backdrop-filter: blur(10px);
  filter: drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.15));
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.4);
`;
const FormContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  flex-direction: column;
  width: ${({theme}) =>
    theme?.isMobile
      ? '90%'
      : theme.dimensions.wdp(isSmallHeight ? 55 : 50) + 'px'};
  padding: ${isSmallHeight ? 1 : 1.5}rem;
  margin-bottom: ${isSmallHeight ? 2 : 4}%;
  margin-top: ${isSmallHeight ? 2 : 4}%;
  background: linear-gradient(
    45deg,
    rgba(219, 203, 171, 0.3) -68.52%,
    ${({theme}) => theme.secondary + '44'} 100%
  );
  filter: drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.15));
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);
  border-radius: 26px;
  z-index: 2;
`;
const InputContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  margin-bottom: ${isSmallHeight ? 2 : 4}%;
  width: 100%;
  flex-wrap: wrap;
  justify-content: space-between;
  @media (min-width: 768px) {
    column-gap: 2%;
  }
`;
const InputWrapper = styled(motion.div)<{isHidden: boolean}>`
  display: ${({isHidden}) => (isHidden ? 'none' : 'flex')};
  width: 100%;
  padding-bottom: ${isSmallHeight ? 10 : 30}px;
  @media (min-width: 768px) {
    min-width: 49%;
    flex: 1;
  }
`;
const Input = styled(GenericInput)`
  .input {
    &::placeholder {
      color: ${({theme}) => theme.text.light};
      opacity: 0.7;
    }
  }
`;
const PickerItem = styled(Picker)`
  border-radius: ${(isSmallHeight ? 37 : 42) / 2}px !important;
  opacity: ${({disabled}) => (disabled ? 0.6 : 1)} !important;

  .react-dropdown-select-type-multi {
    max-height: 5rem;
    overflow-y: auto;
  }
  .react-dropdown-select-item {
    border-bottom: 1px solid ${({theme}) => theme.text.border + '44'};
    padding: 1rem 1.5rem;
    :hover {
      color: ${({theme}) => theme.primary + '77'};
    }
  }
  .react-dropdown-select-input {
    max-width: 100%;
    overflow-x: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    &::placeholder {
      color: ${({theme}) => theme.text.light};
      opacity: 0.75;
    }
  }
  .react-dropdown-select-option {
    background-color: ${({theme}) => theme.primary};
    border-radius: 10px;
    color: ${({theme}) => theme.text.light};
  }
  .react-dropdown-select-dropdown-select-all {
    padding: ${isSmallHeight ? 0.5 : 1}rem ${isSmallHeight ? 1 : 1.5}rem;
    color: ${({theme, list, values}) =>
      (values || []).length === (list || []).length
        ? theme.text.error
        : theme.secondary};
    font-weight: bold;
    margin-top: auto;
  }
  .react-dropdown-select-dropdown-handle {
    padding: 0;
    margin: 0;
  }
`;
const Title = styled.p`
  color: ${({theme}) => theme.text.light};
  font-size: ${({theme}) => theme.text.s6};
  font-weight: 400;
  text-align: center;
  letter-spacing: 1px;
  padding: 0 ${isSmallHeight ? 2 : 4}rem ${isSmallHeight ? 2 : 3}rem
    ${isSmallHeight ? 2 : 4}rem;
  margin: 0;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
`;
const ItemContainer = styled(motion.button)<{checked: boolean}>`
  padding: 0.6rem 1rem;
  background-color: ${({theme, checked, disabled}) =>
    disabled
      ? theme.text.dark + '11'
      : checked
        ? theme.secondary + 'BB'
        : 'transparent'};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border: none;
  margin-top: 2px;
`;
const ItemContentContainer = styled(motion.button)<{checked: boolean}>`
  padding: 0 0.5rem;
  background-color: ${({theme, checked, disabled}) =>
    disabled
      ? theme.text.dark + '11'
      : checked
        ? theme.secondary + 'BB'
        : 'transparent'};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border: none;
  margin-top: 3px;
  margin-right: 5px;
  border-radius: 20px;
`;
const CheckContainer = styled(motion.div)`
  padding: 0.2rem;
`;
const CheckIcon = styled(AiOutlineCheck)`
  color: ${({theme}) => theme.text.light};
  width: ${({theme}) => theme.text.s9};
  height: ${({theme}) => theme.text.s9};
  scale: 1.3;
`;
const LockIcon = styled(AiFillLock)`
  color: ${({theme}) => theme.text.dark};
  width: ${({theme}) => theme.text.s9};
  height: ${({theme}) => theme.text.s9};
  scale: 1.6;
`;
const CloseIcon = styled(AiOutlineClose)`
  color: ${({theme}) => theme.text.light};
  width: ${({theme}) => theme.text.s9};
  height: ${({theme}) => theme.text.s9};
`;

const ItemText = styled(motion.p)<{checked: boolean; disabled: boolean}>`
  color: ${({theme, checked, disabled}) =>
    disabled ? theme.text.grey : checked ? theme.text.light : theme.text.dark};
  font-size: ${({theme}) => theme.text.s8};
  letter-spacing: 1px;
  text-align: left;
  text-shadow: 0 1px 4px
    ${({theme, checked}) =>
      checked ? theme.text.dark + '44' : theme.text.light + '44'};
`;
const BackIconContainer = styled(motion.button)`
  aspect-ratio: 1;
  background: linear-gradient(
    to right,
    ${({theme}) => theme.text.light + '22'},
    ${({theme}) => theme.text.light + '44'}
  );
  backdrop-filter: blur(2px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  margin-right: auto;
  position: absolute;
  top: 1rem;
  left: 1rem;
  width: ${({theme}) => iconSize(theme.isMobile)};
`;
const MuteIconContainer = styled(BackIconContainer)`
  left: auto;
  right: 1rem;
`;

const mandatoryFields = ['domains', 'date', 'text'];

const onBackPress = () => {
  return showGlobalModal({
    title: tr('afterLogin.logoutTitle'),
    message: tr('afterLogin.logoutMessage'),
    onConfirm: () => endSession(),
    type: 'question',
  });
};

export const customItemRenderer = ({item, methods}) => {
  return (
    <ItemContainer
      disabled={item.locked}
      whileTap={{opacity: 0.9}}
      onClick={() => methods.addItem(item)}
      key={item?.id}
      checked={methods.isSelected(item)}
      layout>
      <ItemText
        disabled={item.locked}
        checked={methods.isSelected(item)}
        layout={'preserve-aspect'}>
        {item?.name}
      </ItemText>
      {(methods.isSelected(item) || item?.locked) && (
        <CheckContainer layout>
          {item?.locked ? <LockIcon /> : <CheckIcon />}
        </CheckContainer>
      )}
    </ItemContainer>
  );
};
const customOptionRenderer = ({item, methods}) => {
  if (item.locked) {
    return undefined;
  }
  return (
    <ItemContentContainer
      disabled={item.locked}
      whileTap={{opacity: 0.9}}
      onClick={e => methods.removeItem(e, item)}
      key={item?.id}
      checked={methods.isSelected(item)}
      layout>
      <ItemText
        disabled={item.locked}
        checked={methods.isSelected(item)}
        layout={'preserve-aspect'}>
        {item?.name}
      </ItemText>
      {(methods.isSelected(item) || item?.locked) && (
        <CheckContainer layout>
          <CloseIcon />
        </CheckContainer>
      )}
    </ItemContentContainer>
  );
};

const Researches = () => {
  useSetMainLayoutProps({
    showHeader: false,
    showBack: true,
    showDrawer: false,
    backgroundImage: require('../../../assets/images/scrapeArticle.webp'),
    backgroundMobileImage: require('../../../assets/images/scrapeArticle.webp'),
    hideTopOverlay: true,
  });
  const theme = useTheme() as Theme;
  useScrollTop();
  const formRef = useRef(null);
  const [formWidth, setFormWidth] = useState(0);

  const [form, updateForm] = useState<any>({});
  const [formComplete, setFormComplete] = useState(false);
  const [isFormFinish, setIsFormFinish] = useState(false);
  const [domains, setDomains] = useState<DomainsData[]>([]);
  const [specialities, setSpecialities] = useState<SpecialityData[]>([]);
  const [typeOfStudy, setTypeOfStudy] = useState<TypeOfStudyData[]>([]);
  const [isReady, setIsReady] = useState(false);
  const [isIntroFinished, setIsIntroFinished] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const interactedFields = useRef<Set<string>>(new Set());
  const currentField = useRef<keyof typeof genioResearchFlow>('intro');
  const currentFieldPlay = useRef<keyof typeof genioResearchFlow>('intro');
  const currentFieldEnd = useRef<keyof typeof genioResearchFlow>(null);

  // sound
  const {play, stop, playing, load, mute, seek} = useGlobalAudioPlayer();

  const initPage = async () => {
    const res = await getDomains();
    if (res) setDomains(res);
    const resSpeciality = await getSpeciality();
    if (resSpeciality) setSpecialities(resSpeciality);
    const resTypeOfStudy = await getTypeOfStudy();
    if (resTypeOfStudy) setTypeOfStudy(resTypeOfStudy);
  };
  useEffect(() => {
    if (formRef.current) {
      setFormWidth(formRef.current.offsetWidth);
    }
    const handleResize = () => {
      if (formRef.current) {
        setFormWidth(formRef.current.offsetWidth);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    initPage();
  }, []);

  useEffect(() => {
    if (isReady) {
      currentField.current = getNextResearchField(currentField?.current);
      handleVoice('intro');
    }
  }, [isReady]);

  useEffect(() => {
    if (isMuted) {
      mute(true);
      seek(100000);
    } else {
      mute(false);
    }
  }, [playing]);

  // Handle form value changes and play voice1After upon completion
  const handleScrapeFieldsChange = (label: string, value: any) => {
    handleChange(label, value, updateForm);
    if (currentField?.current && currentField?.current.includes(label)) {
      handleVoice(currentField?.current);
    }
  };

  const handleVoice = (field: any, withPlay = true) => {
    if (!field || interactedFields.current.has(field)) return;
    currentField.current = field;
    const voice = genioResearchFlow[field];
    if (!voice) return;

    // Stop any ongoing playback and load the new voice
    stop();
    load(voice, {
      autoplay: false,
      onplay: () => {
        currentFieldPlay.current = field;
      },
      onend: () => {
        if (
          getFieldResearchIndex(currentFieldEnd.current) <
          getFieldResearchIndex(field)
        ) {
          currentFieldEnd.current = field;
        }
        if (field === 'intro') {
          setIsIntroFinished(true);
        }
        if (
          field === 'domainsBefore' &&
          interactedFields.current.has('finish')
        ) {
          interactedFields.current = new Set<string>();
          interactedFields.current.add('intro');
        }
        interactedFields.current.add(field);

        currentFieldPlay.current = null;
        const nextField = getNextResearchField(field);
        if (nextField) {
          handleVoice(nextField, false);
        }
      },
      onstop: () => {
        currentFieldPlay.current = null;
        if (
          getFieldResearchIndex(currentFieldEnd.current) <
          getFieldResearchIndex(field)
        ) {
          currentFieldEnd.current = field;
        }
        if (
          field === 'domainsBefore' &&
          interactedFields.current.has('finish')
        ) {
          interactedFields.current.delete('finish');
        }
        interactedFields.current.add(field);
      },
    });

    // Play the loaded voice if withPlay is true
    if (withPlay) {
      // interactedFields.current.add(field);
      play();
    }
  };
  useEffect(() => {
    setFormComplete(checkIfFormValid(form, mandatoryFields));
    setIsFormFinish(
      checkIfFormValid(
        form,
        genioResearchFormFields.filter(i => i !== 'text'),
      ),
    );
    if (Array.isArray(form?.subSpecialty) && form.subSpecialty.length > 0) {
      if (Array.isArray(form?.specialty) && form.specialty.length > 0) {
        const validSubSpecialties = form.subSpecialty.filter(subSpecialtyItem =>
          form.specialty.some(
            specialtyItem => subSpecialtyItem.parent_id === specialtyItem.id,
          ),
        );

        if (validSubSpecialties.length !== form.subSpecialty.length) {
          handleScrapeFieldsChange('subSpecialty', validSubSpecialties);
        }
      } else {
        handleScrapeFieldsChange('subSpecialty', []);
      }
    }
  }, [form]);

  const _getPickerList = useCallback(
    (label: string): any[] => {
      switch (label) {
        case 'domains':
          const tempDomains = (domains || []).sort((a, b) => {
            if (a.locked === b.locked) return 0;
            return a.locked ? 1 : -1;
          });
          return tempDomains || [];
        case 'specialty':
          return specialities.filter(i => i.parent_id === null) || [];
        case 'subSpecialty':
          if (Array.isArray(form?.specialty) && form?.specialty?.length > 0) {
            return (
              specialities.filter(item =>
                form?.specialty.some(
                  specialtyItem => item.parent_id === specialtyItem.id,
                ),
              ) || []
            );
          } else {
            return [];
          }
        case 'typeOfStudy':
          return typeOfStudy || [];
        default:
          return [];
      }
    },
    [domains, specialities, typeOfStudy, form],
  );

  const startScraping = async (body: ScrapeBody) => {
    const res = await scrapeArticle({...body, save: true});
    if (res) {
      setIsSubmitted(true);
      updateForm({});
    }
  };
  const submit = async () => {
    const body: ScrapeBody = {
      domains: (form?.domains || []).map(i => i.link),
      query: form?.text || '',
      scrape_date: moment(form?.date, 'LL').format('YYYY-MM-DD') || '',
      speciality_ids: (form?.specialty || []).map(i => i.id),
      subspeciality_ids: (form?.subSpecialty || []).map(i => i.id),
      type_of_study_ids: (form?.typeOfStudy || []).map(i => i.id),
      save: false,
    };
    startScraping({...body, save: true});
  };

  const getNextSpeech = (label: string) => {
    switch (label) {
      case 'domains':
        return 'dateBefore';
      case 'date':
        return 'specialtyBefore';
      case 'specialty':
        return 'subSpecialtyBefore';
      case 'subSpecialty':
        return 'typeOfStudyBefore';
      case 'typeOfStudy':
        return 'typeOfStudyAfter';
    }
  };

  const _renderHandler = ({index, label}) => {
    const isHidden = !interactedFields?.current?.has(
      getNextSpeech(genioResearchFormFields[index]),
    );
    return (
      <HandlerContainer>
        {isHidden ? (
          <GenericButton
            label={'Next'}
            disabled={!(label && (form?.[label] || [])?.length > 0)}
            isSmall
            onPress={() => {
              if (getNextSpeech(label) !== currentFieldPlay?.current) {
                stop();
              }
              handleVoice(getNextSpeech(label));
            }}
          />
        ) : (
          <Icon
            name={'arrowDown'}
            color={theme.text.light}
            style={{marginRight: 6}}
          />
        )}
      </HandlerContainer>
    );
  };

  const _renderItem = (label, index) => {
    getNextSpeech(genioResearchFormFields[index - 1]);
    const isHidden =
      index !== 0 &&
      !interactedFields?.current?.has(
        getNextSpeech(genioResearchFormFields[index - 1]),
      );
    if (label === 'date') {
      return (
        <InputWrapper isHidden={isHidden} layout key={label}>
          <DatePicker
            isRequired
            placeholder={tr(`scrapeArticle.${label}Placeholder`)}
            label={tr(`scrapeArticle.${label}`)}
            value={form[label]}
            onChange={date => handleScrapeFieldsChange(label, date)}
            format={'LL'}
            onDropdownClose={date => {
              if (date) {
                if (currentFieldPlay?.current === 'dateBefore') {
                  stop();
                }
                handleVoice(getNextSpeech(label));
              }
            }}
          />
        </InputWrapper>
      );
    }
    if (label === 'text') {
      return (
        <InputWrapper isHidden={isHidden} layout key={label}>
          <Input
            label={tr(`scrapeArticle.${label}`)}
            placeholder={tr(`scrapeArticle.${label}Placeholder`)}
            inputContainerStyle={{
              borderRadius: 100,
              borderColor: theme.text.light,
            }}
            style={{
              color: theme.text.light,
            }}
            value={form[label] || ''}
            onChange={e => handleScrapeFieldsChange(label, e.target.value)}
          />
        </InputWrapper>
      );
    }

    return (
      <InputWrapper isHidden={isHidden} layout key={label}>
        <PickerItem
          placeholder={
            label === 'subSpecialty' && !form.specialty
              ? tr(`scrapeArticle.${label}Disabled`)
              : tr(`scrapeArticle.${label}Placeholder`)
          }
          list={_getPickerList(label)}
          label={tr(`scrapeArticle.${label}`)}
          labelKey={'name'}
          valueKey={label === 'domains' ? 'link' : 'id'}
          values={form[label] || []}
          onChange={values => {
            let tempValues: any = values;
            if (Array.isArray(values)) {
              tempValues = values.filter(i => !i.locked);
            }
            handleScrapeFieldsChange(label, tempValues);
          }}
          isRequired={mandatoryFields.includes(label)}
          clearRenderer={() => _renderClearPicker(label, form, updateForm)}
          clearable
          name={label}
          disabled={label === 'subSpecialty' && !form.specialty}
          dropdownHandleRenderer={state =>
            _renderHandler({...state, label, index})
          }
          optionRenderer={customOptionRenderer}
          addPlaceholder={label === 'domains' ? tr('search.placeholder') : ''}
          itemRenderer={customItemRenderer}
          multi
          selectAll
        />
      </InputWrapper>
    );
  };

  const onMutePress = () => {
    if (isMuted) {
      mute(false);
      setIsMuted(false);
    } else {
      mute(true);
      seek(100000);
      setIsMuted(true);
    }
  };

  return (
    <Container>
      <ScrapeRobotAnimation
        isRobotSpeaking={
          playing && getNextResearchFieldIndex(currentField?.current) % 2 !== 0
        }
        isGenioRobotSpeaking={
          playing && getNextResearchFieldIndex(currentField?.current) % 2 === 0
        }
        setIsReady={setIsReady}
        formWidth={formWidth}
      />
      <motion.div
        initial={{opacity: 0, display: 'none'}}
        animate={
          !playing && currentField?.current === 'intro' && isReady
            ? {opacity: 1, display: 'flex', pointerEvents: 'auto'}
            : {opacity: 0, display: 'none', pointerEvents: 'none'}
        }
        transition={{
          delay: 1,
          duration: 0.5,
        }}
        style={{
          flex: 1,
          height: '100%',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'absolute',
          zIndex: 99,
        }}>
        <GenericButton
          label={"Let's start"}
          style={{marginLeft: 'auto', marginRight: 'auto'}}
        />
      </motion.div>
      <FormContainer
        initial={{scale: 0, display: 'none'}}
        animate={
          isSubmitted
            ? {scale: 1, display: 'flex'}
            : {scale: 0, display: 'none'}
        }
        transition={{
          duration: isSubmitted ? 1 : 0.1,
          delay: isSubmitted ? 1 : 0,
        }}
        onAnimationComplete={(definition: any) => {
          if (definition.scale === 1) handleVoice(currentField?.current);
        }}
        style={{
          width: theme.dimensions.wdp(
            theme?.isMobile ? (isSubmitted ? 94 : 60) : isSmallHeight ? 33 : 30,
          ),
          aspectRatio: 1,
          overflow: 'hidden',
          borderRadius: '50%',
          padding: 0,
        }}>
        <GenioAnimation />
        <ResultContainer>
          {isSubmitted && (
            <TypingAnimation
              typingSpeed={isMuted ? 25 : 70}
              string={tr('scrapeArticle.successMessage')}
            />
          )}
          {interactedFields.current.has('finish') && (
            <div>
              <GenericButton
                label={'Start Over'}
                onPress={() => {
                  setIsSubmitted(false);
                  interactedFields.current = new Set();
                  currentFieldEnd.current = 'domainsBefore';
                  currentField.current = 'domainsBefore';
                  handleVoice('domainsBefore', false);
                }}
                isSmall
                style={{
                  marginTop: '1rem',
                  padding: '0 1rem',
                  width: '100%',
                }}
              />
              <GenericButton
                label={'View Dashboard'}
                onPress={() => {
                  openUrl(process.env.REACT_APP_DASHBOARD_URL);
                }}
                isSmall
                style={{
                  marginTop: '1rem',
                  padding: '0 1rem',
                  width: '100%',
                }}
              />
            </div>
          )}
        </ResultContainer>
      </FormContainer>
      <FormContainer
        initial={{y: '100vh', scale: 0.5}}
        animate={
          isReady && isIntroFinished
            ? isSubmitted
              ? {y: '0vh', scale: 0, display: 'none'}
              : {y: '0vh', scale: 1, display: 'flex'}
            : {y: '100vh', scale: 0.5, display: 'none'}
        }
        onAnimationComplete={(definition: any) => {
          if (definition.y === '0vh') {
            currentField.current = getNextResearchField(currentField?.current);
            play();
          }
        }}
        transition={{duration: isSubmitted ? 0.5 : 1}}
        ref={formRef}>
        <BackIconContainer
          style={{
            position: 'absolute',
            top: '1rem',
            left: '1rem',
            width: iconSize(theme.isMobile),
          }}
          onClick={onBackPress}
          whileTap={{
            opacity: 0.9,
            scale: 0.9,
            shadow: `0 8px 16px ${theme.shadow}`,
          }}>
          <IoIosArrowBack color={theme.text.light} size={'70%'} />
        </BackIconContainer>
        <Title>{tr('scrapeArticle.title')}</Title>
        <MuteIconContainer
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            width: iconSize(theme.isMobile),
          }}
          onClick={onMutePress}
          whileTap={{
            opacity: 0.9,
            scale: 0.9,
            shadow: `0 8px 16px ${theme.shadow}`,
          }}>
          {isMuted ? (
            <IoVolumeMuteOutline color={theme.text.light} size={'70%'} />
          ) : (
            <IoVolumeHighOutline color={theme.text.light} size={'70%'} />
          )}
        </MuteIconContainer>
        <InputContainer>
          {genioResearchFormFields.map(_renderItem)}
        </InputContainer>
        {isFormFinish && (
          <GenericButton
            isSmall={isSmallHeight}
            disabled={!formComplete}
            onPress={submit}
            label={tr('scrapeArticle.submit')}
            style={{
              marginBottom: isSmallHeight ? '1%' : '4%',
              padding: '0 5%',
            }}
          />
        )}
      </FormContainer>
    </Container>
  );
};

export default Researches;
